import {
  Grid,
  CardContent,
  Box,
  TextField,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GlobalServices } from "../../services/global";
import { Form, FormikProvider, useFormik } from "formik";
import { SubcategorySchema } from "../../utils/Schema";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { trimFormValues } from "../../utils/helpers";

function AddEditSubCategory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { id, categoryid } = useParams();
  const [data, setData] = useState();
  const [globalsData, setGlobalsData] = useState(null);

  // Fetch data for the edit mode
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GlobalServices.getAllGlobalSettings();
        const resData = response?.data?.data || {};
        setGlobalsData(resData);

        if (id && categoryid) {
          const businessTypes = resData.vendorBusinessTypes || [];
          const currentCat = businessTypes[categoryid];
          if (currentCat && currentCat.subcategories) {
            const currentSub = currentCat.subcategories[id];
            if (currentSub) {
              setData({
                name:
                  typeof currentSub === "object" ? (currentSub.title || currentSub.name) : currentSub,
              });
            }
          }
        } else if (categoryid && !id) {
          // We're just adding a subcategory to this parent.
        }
      } catch (error) {
        errorHandler(error);
      }
    };
    fetchData();
  }, [id, categoryid]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: SubcategorySchema,
    initialValues: {
      id: data?._id || "",
      name: data?.name || "",
      status: data?.status || 1,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Trim all string values in the form values
        const trimmedValues = trimFormValues(values);

        if (!globalsData) {
          toast.error("Global data not loaded.");
          setLoading(false);
          return;
        }

        const businessTypes = [...(globalsData.vendorBusinessTypes || [])];
        const currentCat = businessTypes[categoryid];

        if (!currentCat) {
          toast.error("Category not found.");
          setLoading(false);
          return;
        }

        if (!currentCat.subcategories) {
          currentCat.subcategories = [];
        }

        if (id) {
          // Update subcategory
          if (currentCat.subcategories[id] !== undefined) {
            if (typeof currentCat.subcategories[id] === "object") {
              currentCat.subcategories[id].title = trimmedValues.name;
            } else {
              currentCat.subcategories[id] = { title: trimmedValues.name, status: "active", description: "" };
            }
          }
        } else {
          // Create subcategory as object { title, status, description }
          currentCat.subcategories.unshift({ title: trimmedValues.name, status: "active", description: "" });
        }

        businessTypes[categoryid] = currentCat;

        // Format for API: subcategories as array of objects { title, status, description }
        const formattedBusinessTypes = businessTypes.map((item) => {
          const subs = Array.isArray(item.subcategories)
            ? item.subcategories.map((s) => {
              if (typeof s === "object" && s) {
                return { title: s.title || s.name || "", status: s.status || "active", description: s.description || "" };
              }
              return { title: String(s || ""), status: "active", description: "" };
            })
            : [];

          return {
            category: typeof item.category === "object" ? item.category : {
              title: typeof item.category === "string" ? item.category : "Unnamed",
              description: "",
              image: "",
              status: "active",
            },
            subcategories: subs,
          };
        });

        const payload = {
          ...globalsData,
          vendorBusinessTypes: formattedBusinessTypes,
        };

        delete payload._id;
        delete payload.createdAt;
        delete payload.updatedAt;
        delete payload.__v;

        await GlobalServices.updateGlobalSettings(payload);
        toast.success("Subcategory saved successfully", { toastId: "success" });

        navigate(-1);
      } catch (error) {
        console.error(error);
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box>
      <title>{id ? "Edit" : "Add"} Sub Category</title>
      <CardLayout title={`${id ? "Edit" : "Add"} Subcategory`}>
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid size={{ xs: 12, md: 12 }} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Subcategory Name"
                  value={formik.values.name}
                  onChange={(e) => formik.setFieldValue("name", e.target.value)}
                  onBlur={(e) =>
                    formik.setFieldValue("name", e.target.value.trim())
                  }
                  name="name"
                  error={Boolean(formik.touched.name && formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  inputProps={{ maxLength: 50 }}
                  placeholder="e.g., Electric, Gas, Diesel, Hybrid"
                />
              </Grid>

              <Grid size={{ xs: 12 }} md={12}>
                <Box mt={3}>
                  <LoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    loading={loading}
                  >
                    {id ? "Update" : "Add"} Subcategory
                  </LoadingButton>
                </Box>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditSubCategory;
